<script setup>
import { NCard, NSpace, NH1, NH2, NH3, NP, NDivider, 
NButton, NIcon, NGrid, NGridItem, NStatistic, NTag, NGradientText } from 'naive-ui';
import { useI18n } from 'vue-i18n';
import { 
  Languages, Users, Building, Globe, Heart, 
  BookOpen, UserPlus, Sparkles, Bookmark 
} from 'lucide-vue-next';
import { dt } from '@/utils/i18n';

const { t, locale } = useI18n();

// Stats with Lucide icons
const aboutStats = [
  {
    count: 10,
    label: 'about.stats.languagesSupported',
    icon: Languages,
    color: '#36d1dc'
  },
  {
    count: 450,
    label: 'about.stats.communityContributors',
    icon: Users,
    color: '#5b86e5'
  },
  {
    count: 12000,
    label: 'about.stats.healthcareProviders',
    icon: Building,
    color: '#ef473a'
  }
];

// Features icons
const featureIcons = [
  { icon: Languages, class: 'multilingual' },
  { icon: Globe, class: 'crowdsourced' },
  { icon: Sparkles, class: 'personalized' }
];
</script>

<template>
  <div class="about-container">
    <div class="hero-section">
      <n-h1 class="hero-title">
        <n-gradient-text :gradient="{ from: '#36d1dc', to: '#5b86e5', deg: 45 }">
          {{ dt('about.heroTitle') }}
        </n-gradient-text>
      </n-h1>
      <n-p class="hero-subtitle">{{ dt('about.heroSubtitle') }}</n-p>
    </div>

    <n-card class="mission-card">
      <div class="mission-content">
        <div class="mission-text">
          <n-h2 class="section-heading">{{ dt('about.missionTitle') }}</n-h2>
          <n-p class="mission-paragraph">{{ dt('about.missionText') }}</n-p>
          <n-button type="primary" size="large" class="mission-button">
            {{ dt('about.missionCTA') }}
          </n-button>
        </div>
        <div class="mission-visual">
          <div class="pulse-circle"></div>
        </div>
      </div>
    </n-card>

    <div class="stats-section">
      <n-grid :cols="3" :x-gap="16" :collapsed-rows="3" responsive="screen" :collapsed="['xs', 's']">
        <n-grid-item v-for="(stat, index) in aboutStats" :key="index">
          <n-statistic :label="dt(stat.label)" :value="stat.count">
            <template #prefix>
              <component :is="stat.icon" :size="24" :color="stat.color" />
            </template>
          </n-statistic>
        </n-grid-item>
      </n-grid>
    </div>

    <div class="features-section">
      <n-h2 class="section-title">{{ dt('about.featuresTitle') }}</n-h2>
      <div class="features-grid">
        <div class="feature-item" v-for="(item, index) in featureIcons" :key="index">
          <div :class="`feature-icon ${item.class}`">
            <component :is="item.icon" size="32" color="#ffffff" />
          </div>
          <n-h3 class="feature-heading">{{ dt(`about.features.${['multilingualTitle', 'crowdsourcedTitle', 'personalizedTitle'][index]}`) }}</n-h3>
          <n-p class="feature-text">{{ dt(`about.features.${['multilingualText', 'crowdsourcedText', 'personalizedText'][index]}`) }}</n-p>
          <div class="language-tags" v-if="index === 0">
            <n-tag type="success" size="small">हिन्दी</n-tag>
            <n-tag type="info" size="small">বাংলা</n-tag>
            <n-tag type="warning" size="small">తెలుగు</n-tag>
            <n-tag type="error" size="small">தமிழ்</n-tag>
            <n-tag type="success" size="small">മലയാളം</n-tag>
          </div>
        </div>
      </div>
    </div>

     <!-- Architecture & Privacy Section -->
    <n-card class="architecture-card">
      <n-h2 class="section-title">{{ dt('about.architectureTitle') }}</n-h2>
      <img src="@/assets/images/A_detailed_infographic_in_flowchart_style_depicts.png" 
           alt="Medicko Architecture Diagram" 
           style="width:100%; max-width:1024px; margin:20px 0;" />
      <n-p class="architecture-description">
        {{ dt('about.architectureDescription') }}
      </n-p>
    </n-card>

    <div class="how-it-works">
      <n-h2 class="section-title">{{ dt('about.howItWorksTitle') }}</n-h2>
      <div class="steps-container">
        <div class="step">
          <div class="step-number">1</div>
          <n-h3 class="step-heading">{{ dt('about.steps.step1Title') }}</n-h3>
          <n-p class="step-text">{{ dt('about.steps.step1Text') }}</n-p>
        </div>
        <div class="step-connector"></div>
        <div class="step">
          <div class="step-number">2</div>
          <n-h3 class="step-heading">{{ dt('about.steps.step2Title') }}</n-h3>
          <n-p class="step-text">{{ dt('about.steps.step2Text') }}</n-p>
        </div>
        <div class="step-connector"></div>
        <div class="step">
          <div class="step-number">3</div>
          <n-h3 class="step-heading">{{ dt('about.steps.step3Title') }}</n-h3>
          <n-p class="step-text">{{ dt('about.steps.step3Text') }}</n-p>
        </div>
      </div>
    </div>

    <div class="join-section">
      <n-card class="join-card">
        <n-h2 class="join-heading">{{ dt('about.joinTitle') }}</n-h2>
        <n-p class="join-text">{{ dt('about.joinText') }}</n-p>
        <n-button type="primary" size="large" class="join-button">
          {{ dt('about.joinCTA') }}
        </n-button>
      </n-card>
    </div>
  </div>
</template>

<style scoped>
/* Global text styles to ensure proper wrapping */
:deep(.n-h1), 
:deep(.n-h2), 
:deep(.n-h3), 
:deep(.n-p),
:deep(.n-gradient-text) {
  word-wrap: break-word !important;
  overflow-wrap: break-word !important;
  word-break: break-word !important;
  white-space: normal !important;
  max-width: 100% !important;
  hyphens: auto !important;
}

:deep(.n-statistic-label) {
  white-space: normal !important;
  word-break: break-word !important;
}

.hero-title,
.section-heading,
.feature-heading,
.step-heading,
.join-heading {
  width: 100%;
  max-width: 100%;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
}

.about-container {
  padding: 16px;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  color: #333;
  overflow-x: hidden;
  box-sizing: border-box;
}

.hero-section {
  text-align: center;
  padding: 40px 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.hero-subtitle {
  font-size: 1.2rem;
  margin-top: 12px;
  color: #555;
  max-width: 100%;
}

.mission-card {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 40px;
  width: 100%;
  box-sizing: border-box;
}

.mission-content {
  display: flex;
  align-items: center;
  min-height: 250px;
  width: 100%;
}

.mission-text {
  flex: 1;
  padding: 24px;
  max-width: 100%;
}

.mission-paragraph {
  font-size: 1rem;
  line-height: 1.6;
  margin: 16px 0;
  max-width: 100%;
}

.mission-visual {
  flex: 1;
  position: relative;
  height: 250px;
  background: linear-gradient(135deg, #36d1dc, #5b86e5);
  display: flex;
  align-items: center;
  justify-content: center;
}

.pulse-circle {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  position: relative;
  animation: pulse 2s infinite;
}

.pulse-circle::after {
  content: "";
  position: absolute;
  width: 70%;
  height: 70%;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  top: 15%;
  left: 15%;
}

.mission-button {
  margin-top: 16px;
  background: linear-gradient(135deg, #36d1dc, #5b86e5);
  border: none;
}

.stats-section {
  margin: 40px 0;
  width: 100%;
  box-sizing: border-box;
}

.section-title {
  text-align: center;
  margin-bottom: 30px;
  position: relative;
  font-size: 1.5rem;
  width: 100%;
  max-width: 100%;
}

.section-title::after {
  content: "";
  display: block;
  width: 50px;
  height: 3px;
  background: linear-gradient(135deg, #36d1dc, #5b86e5);
  margin: 10px auto 0;
  border-radius: 2px;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin: 30px 0 40px;
  width: 100%;
  box-sizing: border-box;
}

.feature-item {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s, box-shadow 0.3s;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
}

.feature-text {
  width: 100%;
  word-break: break-word;
  overflow-wrap: break-word;
}

.feature-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.feature-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  flex-shrink: 0;
}

.multilingual {
  background: linear-gradient(135deg, #36d1dc, #5b86e5);
}

.crowdsourced {
  background: linear-gradient(135deg, #11998e, #38ef7d);
}

.personalized {
  background: linear-gradient(135deg, #fc4a1a, #f7b733);
}

.language-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
}

.how-it-works {
  margin: 40px 0;
  width: 100%;
  box-sizing: border-box;
}

.steps-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 30px;
  width: 100%;
  box-sizing: border-box;
}

.step {
  flex: 1;
  text-align: center;
  padding: 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  position: relative;
  z-index: 1;
  min-width: 0; /* Allow shrinking below content size */
  max-width: 100%;
  box-sizing: border-box;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
}

.step-number {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #36d1dc, #5b86e5);
  border-radius: 50%;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: bold;
  margin: 0 auto 16px;
}

.step-connector {
  flex: 0.5;
  height: 3px;
  background: linear-gradient(90deg, #36d1dc, #5b86e5);
  position: relative;
  z-index: 0;
}

.join-section {
  margin: 40px 0;
  width: 100%;
  box-sizing: border-box;
}

.join-card {
  text-align: center;
  padding: 30px 20px;
  background: linear-gradient(135deg, rgba(54, 209, 220, 0.1), rgba(91, 134, 229, 0.1));
  border-radius: 16px;
  width: 100%;
  box-sizing: border-box;
}

.join-text {
  max-width: 700px;
  margin: 16px auto;
  font-size: 1rem;
}

.join-button {
  margin-top: 16px;
  background: linear-gradient(135deg, #36d1dc, #5b86e5);
  border: none;
  font-size: 1rem;
  padding: 0 30px;
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 10px rgba(255, 255, 255, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
  }
}

@media screen and (max-width: 768px) {
  .about-container {
    padding: 12px;
  }
  
  .hero-section {
    padding: 30px 0;
  }
  
  .hero-subtitle {
    font-size: 1rem;
  }
  
  .mission-content {
    flex-direction: column;
  }
  
  .mission-visual {
    width: 100%;
    height: 200px;
  }
  
  .mission-text {
    padding: 20px;
  }
  
  .steps-container {
    flex-direction: column;
    gap: 20px;
  }
  
  .step-connector {
    width: 3px;
    height: 20px;
  }
  
  .feature-item {
    padding: 20px;
  }
  
  .section-title {
    font-size: 1.3rem;
    margin-bottom: 20px;
  }
  
  .mission-paragraph, .join-text {
    font-size: 0.95rem;
  }
}

@media screen and (max-width: 480px) {
  .pulse-circle {
    width: 100px;
    height: 100px;
  }
  
  .feature-icon {
    width: 50px;
    height: 50px;
  }
  
  .step-number {
    width: 36px;
    height: 36px;
    font-size: 1.1rem;
  }
  
  .join-card {
    padding: 20px 16px;
  }
  
  .section-title {
    font-size: 1.2rem;
  }
}
</style>